import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Safely parse any date value (e.g. 'Oct 18, 2026', ISO string, timestamp) into 'YYYY-MM-DD'
 * Returns current date in YYYY-MM-DD if unparseable or empty.
 */
export function parseToIsoDate(dateVal?: string | number | Date | null): string {
  const today = new Date().toISOString().slice(0, 10);
  if (!dateVal) return today;
  try {
    const s = String(dateVal).trim();
    if (s.includes("T") || (s.includes("-") && s.length >= 10)) {
      const firstT = s.split("T")[0] ?? "";
      const part = firstT.split(" ")[0] ?? "";
      if (part.length === 10 && (part.match(/-/g) || []).length === 2) {
        return part;
      }
    }
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      return d.toISOString().slice(0, 10);
    }
  } catch {
    // fallback to current date
  }
  return today;
}

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Standardize article publication / modified dates to 'MMM d, yyyy' across the entire platform
 * Examples: 'Oct 25, 2026', 'Oct 24, 2026'
 */
export function formatDisplayDate(dateVal?: string | number | Date | null): string {
  if (!dateVal) return "";
  const s = String(dateVal).trim();
  try {
    const iso = parseToIsoDate(s);
    const [y, m, d] = iso.split("-").map(Number);
    if (y && m && d) {
      const month = MONTH_NAMES[m - 1] || "Oct";
      return `${month} ${d}, ${y}`;
    }
    const parsed = new Date(s);
    if (!isNaN(parsed.getTime())) {
      return `${MONTH_NAMES[parsed.getUTCMonth()]} ${parsed.getUTCDate()}, ${parsed.getUTCFullYear()}`;
    }
  } catch {
    // fallback to original trimmed string
  }
  return s;
}

/**
 * Standardize article read-time to 'X min read' across the entire platform
 * Examples: '9 min read', '8 min read'
 */
export function formatReadTime(readVal?: string | number | null): string {
  if (!readVal) return "5 min read";
  const s = String(readVal).trim();
  const match = s.match(/\d+/);
  const minutes = match ? match[0] : "5";
  return `${minutes} min read`;
}
